// Documents section of the patient file: draft a referral letter, a letter to
// the referring doctor or a progress report from the patient's notes, review
// and edit it, then email it, download it as PDF or Word, or file it in
// Patient Files. Email and Save to Files put a PDF copy in Patient Files (the
// server does that in /export); a download on its own does not.
//
// Two screens, no wizard chrome:
//   Set up  : type picker, language, which notes to include (Last note is
//             the opened appointment's note alone; All history and Custom
//             are exactly that window), type-specific fields, one Generate
//             button.
//   Review  : editable draft on a letterhead preview, Regenerate, and the
//             download bar (sticky at the bottom on a phone).
//
// Rendered inside the rail content area by Session.jsx, only for users that
// pass userCanGenerateDocuments(): the AIHistory flag plus the Practitioner
// role. The author is always the logged-in practitioner.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Container, Row, Col, Button, Form, Spinner, Alert, OverlayTrigger, Tooltip, Badge,
} from "react-bootstrap";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { jwtDecode } from "jwt-decode";
import { saveAs } from "file-saver";
import {
  getDocumentSources, draftDocument, exportDocument, getPracticeById, fetchPracticeTemplates, getDocumentDraft,
  listDocumentDrafts,
} from "../../api";
import { getCasesByPatientId } from "../../acases";
import { getPeopleForUser } from "../../apeople";
import { downloadDocumentDocx } from "./exportDocx";
import RecipientSearch, { personDisplayName, personDetail } from "./RecipientSearch";
import SendDocumentModal, { personEmails } from "./SendDocumentModal";
import {
  DOCUMENT_TYPES, RANGE_OPTIONS, LANGUAGE_OPTIONS, DEFAULT_REPORT_HEADINGS, FITNESS_OPTIONS, MODALITY_OPTIONS,
  typeByKey, formatDateGB, todayISO,
} from "./documentTypes";
import AppToast from "../Toasts/AppToast";
import { useToast } from "../Toasts/useToast";

// Every text button on the panel is this wide, so the type picker, the
// option pickers, Generate and the download bar all read as one set, and the
// seven type buttons stay on one row on a laptop. Sized to the longest label,
// "Download Word" with its icon, which measures 148px in Poppins SemiBold at
// the .acc-list button size, so the .acc-list .btn ellipsis never kicks in.
// Keep new labels at or under that. The one exception is the small "+ Add"
// next to the section input on the Progress report form.
const BUTTON_WIDTH = 150;
const ADD_BUTTON_WIDTH = 90;

function currentUserId() {
  try {
    const token = sessionStorage.getItem("user");
    return token ? jwtDecode(token)?._id || null : null;
  } catch (_e) {
    return null;
  }
}

// The body is edited as ONE rich-text memo, the same editor as the Note kind
// on the patient's notes, pre-filled with the drafted sections: each heading
// as a bold line, its paragraphs beneath. The server, the PDF and the Word
// file keep the structured sections[] shape, so these two convert between
// the editor's HTML and that shape. The rule is simple and visible to the
// user: a line that is entirely bold is a section heading; everything under
// it, until the next bold line, is that section's paragraphs. Text before
// the first heading becomes an untitled section.
const escapeHtml = (t) =>
  String(t ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const sectionsToHtml = (sections) =>
  (sections || [])
    .map((s) => {
      const parts = [];
      if (s.heading) parts.push(`<p><strong>${escapeHtml(s.heading)}</strong></p>`);
      for (const para of s.paragraphs || []) parts.push(`<p>${escapeHtml(para)}</p>`);
      return parts.join("");
    })
    .join("");

// A block whose whole visible text sits inside <strong>/<b> is a heading.
const isHeadingBlock = (el) => {
  const text = (el.textContent || "").trim();
  if (!text) return false;
  const bold = Array.from(el.querySelectorAll("strong, b"))
    .map((b) => (b.textContent || "").trim())
    .join(" ")
    .replace(/\s+/g, " ");
  return bold.replace(/\s+/g, " ") === text.replace(/\s+/g, " ");
};

const htmlToSections = (html) => {
  if (typeof window === "undefined" || !window.DOMParser) return [];
  const doc = new window.DOMParser().parseFromString(`<div>${html || ""}</div>`, "text/html");
  const root = doc.body.firstElementChild;
  const sections = [];
  let current = null;
  const push = (text) => {
    const t = text.replace(/\s+/g, " ").trim();
    if (!t) return;
    if (!current) current = { heading: "", paragraphs: [] };
    current.paragraphs.push(t);
  };
  for (const el of Array.from(root?.children || [])) {
    const tag = el.tagName.toLowerCase();
    if (tag === "ol" || tag === "ul") {
      Array.from(el.children).forEach((li, i) => {
        push(`${tag === "ol" ? `${i + 1}.` : "\u2022"} ${li.textContent || ""}`);
      });
      continue;
    }
    if (isHeadingBlock(el)) {
      if (current && (current.heading || current.paragraphs.length)) sections.push(current);
      current = { heading: (el.textContent || "").replace(/\s+/g, " ").trim(), paragraphs: [] };
      continue;
    }
    // Quill writes one <p> per line; a soft break (<br>) splits a paragraph.
    const lines = (el.innerHTML || "").split(/<br\s*\/?>/i);
    if (lines.length > 1) {
      const scratch = doc.createElement("div");
      for (const l of lines) {
        scratch.innerHTML = l;
        push(scratch.textContent || "");
      }
    } else {
      push(el.textContent || "");
    }
  }
  if (current && (current.heading || current.paragraphs.length)) sections.push(current);
  return sections;
};

// The editor's content, for change detection.
const editorSnapshot = (d) => JSON.stringify({
  title: d.title, recipientName: d.recipientName, recipientDetail: d.recipientDetail,
  salutation: d.salutation, closing: d.closing, body: d.body || "",
});

// Bold marks a heading, lists are kept; nothing else is offered because the
// PDF and the Word file are plain text and would silently drop it.
const BODY_TOOLBAR = { toolbar: [["bold"], [{ list: "ordered" }, { list: "bullet" }]] };
const BODY_FORMATS = ["bold", "list", "bullet"];

const norm = (s) => String(s || "").trim().toLowerCase();

const personIdOf = (p) => (p?._id?.$oid || p?._id ? String(p._id?.$oid || p._id) : null);

// A name as people write it, without the title and punctuation: "Dr A.
// Specialist" and "dr a specialist" are the same person.
const nameKey = (s) =>
  norm(s)
    .replace(/\b(dr|mr|mrs|ms|miss|prof|sr)\.?\b/g, " ")
    .replace(/[.,]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

// The People record a typed or carried-over recipient name refers to. Exact
// on the full name first; failing that, the one person whose surname and
// first initial match ("Dr A Specialist" against "Dr Anton Specialist").
// Null when nobody or more than one person fits: a guess is worse than an
// empty To field.
const findPersonByName = (people, name) => {
  const key = nameKey(name);
  if (!key) return null;
  const exact = people.find((p) => nameKey(personDisplayName(p)) === key || nameKey(`${p.firstName} ${p.lastName}`) === key);
  if (exact) return exact;
  const parts = key.split(" ");
  if (parts.length < 2) return null;
  const initial = parts[0][0];
  const surname = parts[parts.length - 1];
  const loose = people.filter((p) => nameKey(p.lastName) === surname && nameKey(p.firstName).startsWith(initial));
  return loose.length === 1 ? loose[0] : null;
};

function base64ToBlob(b64, contentType) {
  const bytes = atob(b64);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
  return new Blob([arr], { type: contentType });
}

// openDraftId: a draft the server prepared on its own after a SyncTo note
// was applied (a task's "Open Document" lands here with it). The panel opens
// straight on the review screen with that draft loaded.
export default function DocumentsPanel({ patientId, practiceId, appointmentId, patient, openDraftId = null }) {
  const { toast, showToast, hideToast } = useToast();

  // ── Set up state ──────────────────────────────────────────────────────────
  const [step, setStep] = useState("setup");
  const [typeKey, setTypeKey] = useState(DOCUMENT_TYPES[0].key);
  const type = typeByKey(typeKey);
  const [language, setLanguage] = useState("en");
  const [rangeMode, setRangeMode] = useState(type.defaultRange);
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [recipientName, setRecipientName] = useState("");
  // The People record behind the recipient, when one was picked rather than
  // typed. It makes the email address lookup exact; a typed name falls back
  // to matching by name.
  const [recipientPersonId, setRecipientPersonId] = useState(null);
  const [recipientDetail, setRecipientDetail] = useState("");
  const [ask, setAsk] = useState("");
  const [urgency, setUrgency] = useState("routine");
  const [headings, setHeadings] = useState(DEFAULT_REPORT_HEADINGS);
  const [newHeading, setNewHeading] = useState("");
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [referrer, setReferrer] = useState(null);
  const [people, setPeople] = useState([]);
  // Sick note and return to work
  const [examinedOn, setExaminedOn] = useState(todayISO());
  const [unfitFrom, setUnfitFrom] = useState(todayISO());
  const [unfitTo, setUnfitTo] = useState(todayISO());
  const [fitness, setFitness] = useState("unfit");
  const [includeCondition, setIncludeCondition] = useState(false);
  const [conditionText, setConditionText] = useState("");
  const [fitFrom, setFitFrom] = useState(todayISO());
  const [restrictions, setRestrictions] = useState("");
  // Motivation
  const [requestFor, setRequestFor] = useState("");
  const [referenceNumber, setReferenceNumber] = useState("");
  const [justification, setJustification] = useState("");
  // Imaging request
  const [modality, setModality] = useState("X-ray");
  const [region, setRegion] = useState("");
  const [clinicalQuestion, setClinicalQuestion] = useState("");
  const [errorCode, setErrorCode] = useState(null);

  const [sourceInfo, setSourceInfo] = useState(null);
  const [sourceLoading, setSourceLoading] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [error, setError] = useState(null);

  // ── Review state ──────────────────────────────────────────────────────────
  const [result, setResult] = useState(null);      // server response from /draft
  const [edited, setEdited] = useState(null);      // editable copy of result.draft
  const pristineRef = useRef("");                   // editor state as loaded, for isDirty
  const [exporting, setExporting] = useState(null); // "pdf" | "docx" | "save" | "email" | null
  const [emailPrefill, setEmailPrefill] = useState(null); // open the Send modal with these fields
  const [draftId, setDraftId] = useState(null);     // set while reviewing a saved draft (SyncTo or phone)
  const [loadingDraft, setLoadingDraft] = useState(false);
  const practiceRef = useRef(null);

  const patientName = useMemo(
    () => [patient?.patientFirstName, patient?.patientLastName].filter(Boolean).join(" "),
    [patient],
  );

  // Referring doctor: the latest case that names one, else the free-text
  // field on the patient record. Pre-fills the recipient for letters.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      let found = null;
      try {
        const res = await getCasesByPatientId(patientId);
        const cases = Array.isArray(res?.data) ? res.data : [];
        const withDoctor = cases.find((c) => c.referringDoctorName);
        if (withDoctor) {
          found = {
            name: withDoctor.referringDoctorName,
            hpcsa: withDoctor.referringDoctorHPCSA || "",
            detail: [withDoctor.referringDoctorHPCSA && `HPCSA ${withDoctor.referringDoctorHPCSA}`].filter(Boolean).join(", "),
          };
        }
      } catch (_e) { /* cases are a convenience; the recipient stays blank */ }
      if (!found && patient?.patientReferredBy) found = { name: patient.patientReferredBy, detail: "" };
      if (!cancelled) setReferrer(found);
    })();
    return () => { cancelled = true; };
  }, [patientId, patient?.patientReferredBy]);

  // The practice's People list backs the recipient typeahead, the same list
  // the Session email "To" field searches. Scoped server-side to the user's
  // practices; getPeopleForUser throws on 404 when there are none.
  useEffect(() => {
    let cancelled = false;
    const token = sessionStorage.getItem("user");
    if (!token) return undefined;
    (async () => {
      try {
        const list = await getPeopleForUser(token);
        if (!cancelled) setPeople(Array.isArray(list) ? list : []);
      } catch (_e) {
        if (!cancelled) setPeople([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // The referrer as the recipient fields should show it: the matching
  // People record when there is one (by HPCSA number, else by name), so the
  // discipline fills in too; otherwise what the case or patient record says.
  const referrerFill = useCallback(() => {
    if (!referrer) return null;
    const match = people.find((p) =>
      (referrer.hpcsa && norm(p.hpcsaNumber) === norm(referrer.hpcsa)) ||
      norm(personDisplayName(p)) === norm(referrer.name) ||
      norm(`${p.firstName} ${p.lastName}`) === norm(referrer.name));
    return match
      ? { name: personDisplayName(match), detail: personDetail(match) || referrer.detail }
      : { name: referrer.name, detail: referrer.detail };
  }, [referrer, people]);

  const pickRecipient = (p) => {
    setRecipientName(personDisplayName(p));
    setRecipientDetail(personDetail(p));
    setRecipientPersonId(personIdOf(p));
  };

  // Report Export templates give the practice its own headings.
  useEffect(() => {
    let cancelled = false;
    const uid = currentUserId();
    if (!uid) return undefined;
    (async () => {
      try {
        const all = await fetchPracticeTemplates(uid);
        const mine = (Array.isArray(all) ? all : []).filter(
          (t) => t.templateInitial === "reportexport" &&
            (!practiceId || (t.templatePracticeId || []).map(String).includes(String(practiceId))),
        );
        if (!cancelled) setTemplates(mine);
      } catch (_e) { /* no templates: the default headings stay */ }
    })();
    return () => { cancelled = true; };
  }, [practiceId]);

  // Changing the type resets what depends on it.
  const chooseType = (key) => {
    const next = typeByKey(key);
    setTypeKey(key);
    setRangeMode(next.defaultRange);
    setError(null);
    const fill = next.hasRecipient && !recipientName ? referrerFill() : null;
    if (fill) {
      setRecipientName(fill.name);
      setRecipientDetail(fill.detail);
    }
  };

  // Prefill once the referrer is known. People usually arrive after the
  // case does, so a second pass upgrades a bare name to the People record's
  // name and discipline as long as the practitioner has not typed over it.
  useEffect(() => {
    const fill = referrer && type.hasRecipient ? referrerFill() : null;
    if (!fill) return;
    if (!recipientName) {
      setRecipientName(fill.name);
      setRecipientDetail(fill.detail);
    } else if (norm(recipientName) === norm(referrer.name) && (!recipientDetail || recipientDetail === referrer.detail)) {
      setRecipientName(fill.name);
      setRecipientDetail(fill.detail);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referrer, people]);

  const applyTemplate = (id) => {
    setTemplateId(id);
    const t = templates.find((x) => x._id === id);
    if (!t) { setHeadings(DEFAULT_REPORT_HEADINGS); return; }
    const fromNotes = (t.notes || [])
      .filter((n) => n.sessionsFieldType === "text" || n.sessionsFieldType === "textarea")
      .map((n) => n.sessionsSection)
      .filter(Boolean);
    setHeadings(fromNotes.length ? fromNotes : DEFAULT_REPORT_HEADINGS);
  };

  const range = useMemo(
    () => ({ mode: rangeMode, from: rangeMode === "custom" ? customFrom : undefined, to: rangeMode === "custom" ? customTo : undefined }),
    [rangeMode, customFrom, customTo],
  );
  const customIncomplete = rangeMode === "custom" && (!customFrom || !customTo);

  // Live note count for the range. Debounced so typing a custom date does
  // not fire a request per keystroke.
  useEffect(() => {
    if (!patientId || !practiceId || customIncomplete) return undefined;
    let cancelled = false;
    setSourceLoading(true);
    const t = setTimeout(async () => {
      try {
        const info = await getDocumentSources(patientId, { practiceId, range, appointmentId });
        if (!cancelled) { setSourceInfo(info); setError(null); }
      } catch (e) {
        if (!cancelled) { setSourceInfo(null); setError(e.message); }
      } finally {
        if (!cancelled) setSourceLoading(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [patientId, practiceId, appointmentId, range, customIncomplete]);

  const inputs = useMemo(() => {
    switch (typeKey) {
      case "progress_report": return { headings };
      case "referral": return { recipientName, recipientDetail, ask, urgency };
      case "sick_note": return { examinedOn, unfitFrom, unfitTo, fitness, includeCondition, conditionText };
      case "return_to_work": return { examinedOn, fitFrom, restrictions };
      case "motivation": return { requestFor, referenceNumber, justification };
      case "imaging_request": return { recipientName, recipientDetail, modality, region, clinicalQuestion };
      default: return { recipientName, recipientDetail };
    }
  }, [typeKey, headings, recipientName, recipientDetail, ask, urgency, examinedOn, unfitFrom, unfitTo, fitness,
    includeCondition, conditionText, fitFrom, restrictions, requestFor, referenceNumber, justification,
    modality, region, clinicalQuestion]);

  // Form-built types stand on their inputs, so they do not need a saved note.
  const formIncomplete =
    (typeKey === "sick_note" && (!examinedOn || !unfitFrom || !unfitTo || unfitTo < unfitFrom)) ||
    (typeKey === "return_to_work" && (!examinedOn || !fitFrom));
  const canGenerate = !drafting && !sourceLoading && !customIncomplete && !formIncomplete &&
    (type.form || (sourceInfo?.count || 0) > 0);

  const generate = useCallback(async () => {
    setDrafting(true);
    setError(null);
    setErrorCode(null);
    try {
      const res = await draftDocument(patientId, { practiceId, type: typeKey, language, range, appointmentId, inputs });
      // A fresh draft replaces the saved one on screen; exporting it will
      // not close the saved draft's task, which stays open until that draft
      // itself is sent.
      setDraftId(null);
      setResult(res);
      // The picked People record rides along while the draft kept the name
      // it was given; a model that reworded the addressee gets a name match
      // at email time instead.
      const loaded = {
        ...res.draft,
        body: sectionsToHtml(res.draft.sections),
        recipientPersonId: recipientPersonId && nameKey(res.draft.recipientName) === nameKey(recipientName) ? recipientPersonId : null,
      };
      setEdited(loaded);
      pristineRef.current = editorSnapshot(loaded);
      setStep("review");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setError(e.message);
      setErrorCode(e.code || null);
    } finally {
      setDrafting(false);
    }
  }, [patientId, practiceId, typeKey, language, range, appointmentId, inputs, recipientName, recipientPersonId]);

  // Unsent drafts waiting on this practitioner: SyncTo's automated drafts and
  // drafts started on the phone. Refreshed whenever the set-up screen shows.
  const [openDrafts, setOpenDrafts] = useState([]);
  const [draftToLoad, setDraftToLoad] = useState(openDraftId);
  useEffect(() => { if (openDraftId) setDraftToLoad(openDraftId); }, [openDraftId]);
  useEffect(() => {
    if (step !== "setup" || !patientId || !practiceId) return undefined;
    let cancelled = false;
    listDocumentDrafts(patientId, { practiceId })
      .then((rows) => { if (!cancelled) setOpenDrafts(rows); })
      .catch(() => { if (!cancelled) setOpenDrafts([]); });
    return () => { cancelled = true; };
  }, [step, patientId, practiceId]);

  // Load a saved draft straight into the review screen. The set-up state is
  // filled from the draft too, so Regenerate and Back behave as if the
  // practitioner had generated it here.
  useEffect(() => {
    if (!draftToLoad || !patientId || !practiceId) return undefined;
    let cancelled = false;
    setLoadingDraft(true);
    setError(null);
    (async () => {
      try {
        const res = await getDocumentDraft(patientId, draftToLoad, { practiceId });
        if (cancelled) return;
        const next = typeByKey(res.type);
        setTypeKey(next.key);
        setLanguage(res.language === "af" ? "af" : "en");
        setRangeMode(res.range?.mode && RANGE_OPTIONS.some((r) => r.key === res.range.mode) ? res.range.mode : next.defaultRange);
        const inp = res.inputs || {};
        if (inp.recipientName != null) setRecipientName(inp.recipientName);
        if (inp.recipientDetail != null) setRecipientDetail(inp.recipientDetail);
        if (inp.ask != null) setAsk(inp.ask);
        if (inp.urgency) setUrgency(inp.urgency);
        if (inp.examinedOn) setExaminedOn(inp.examinedOn);
        if (inp.unfitFrom) setUnfitFrom(inp.unfitFrom);
        if (inp.unfitTo) setUnfitTo(inp.unfitTo);
        if (inp.fitness) setFitness(inp.fitness);
        if (inp.fitFrom) setFitFrom(inp.fitFrom);
        if (inp.restrictions != null) setRestrictions(inp.restrictions);
        const loaded = { ...res.draft, body: sectionsToHtml(res.draft?.sections) };
        setResult(res);
        setEdited(loaded);
        pristineRef.current = editorSnapshot(loaded);
        setDraftId(res.draftId);
        setStep("review");
        window.scrollTo({ top: 0, behavior: "smooth" });
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) { setLoadingDraft(false); setDraftToLoad(null); }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftToLoad, patientId, practiceId]);

  const errorAlert = error && (
    <Alert variant="danger" className="py-2">
      {error}
      {errorCode === "missing-registration" && (
        <>
          {" "}
          <Alert.Link href="/userprofile">Open your profile</Alert.Link>
        </>
      )}
    </Alert>
  );

  // ── Review helpers ────────────────────────────────────────────────────────
  const draftForExport = () => ({
    title: edited.title,
    recipientName: edited.recipientName,
    recipientDetail: edited.recipientDetail,
    salutation: edited.salutation,
    sections: htmlToSections(edited.body),
    closing: edited.closing,
  });

  // True once the practitioner has changed anything since the draft came back.
  // Compares editor state with a snapshot taken as the draft loaded, so the
  // HTML-to-sections conversion in draftForExport cannot make an untouched
  // draft look edited.
  const isDirty = () => !!edited && editorSnapshot(edited) !== pristineRef.current;

  const regenerate = () => {
    if (isDirty() && !window.confirm("Regenerating replaces the edits you have made to this draft. Continue?")) return;
    generate();
  };

  const loadPractice = async () => {
    if (practiceRef.current) return practiceRef.current;
    try { practiceRef.current = await getPracticeById(practiceId); }
    catch (_e) { practiceRef.current = {}; }
    return practiceRef.current;
  };

  const doExport = async (format) => {
    const draft = draftForExport();
    if (!draft.sections.length) { showToast("error", "The document has no content to export."); return; }
    setExporting(format);
    try {
      // "save" files the PDF in Patient Files without a download. pdf and
      // docx only download: filing is a separate, deliberate step.
      const res = await exportDocument(patientId, {
        practiceId, type: typeKey, draft, format, range,
        appointmentId: result?.appointmentId || appointmentId, language, draftId,
      });
      if (format === "pdf") {
        saveAs(base64ToBlob(res.pdfBase64, "application/pdf"), res.fileName);
      } else if (format === "docx") {
        const practice = await loadPractice();
        await downloadDocumentDocx({
          practice, patient, draft, signoff: res.signoff, fileName: res.fileName, date: res.date, language: res.language || language,
        });
      }
      showToast("success", format === "save" ? `Saved to Patient Files as ${res.fileName}` : `Downloaded ${res.fileName}`);
    } catch (e) {
      showToast("error", e.message || "Failed to export the document");
    } finally {
      setExporting(null);
    }
  };

  // Email: open the Send modal addressed to the letter's recipient. The
  // People record that matches the "To" name supplies the address (extra
  // addresses go to CC), and the patient is copied in, since the letter is
  // about them. The practitioner can add themselves in the CC field.
  // Subject and message mirror the server's defaults, so what the
  // practitioner reads here is what goes out if they leave them alone.
  const openEmail = async () => {
    const draft = draftForExport();
    if (!draft.sections.length) { showToast("error", "The document has no content to send."); return; }
    // To: the addressee's People record, by id when it was picked, else by
    // name. CC: the patient, who is usually copied on a letter about them,
    // then any further addresses on the People record.
    const match = (edited.recipientPersonId && people.find((p) => personIdOf(p) === edited.recipientPersonId))
      || findPersonByName(people, edited.recipientName);
    const [primary, ...extras] = match ? personEmails(match) : [];
    const patientEmail = String(patient?.patientEmail || "").trim();
    const practice = await loadPractice();
    const signoff = result?.signoff || {};
    const who = patientName || "the patient";
    const greeting = edited.recipientName ? `Dear ${edited.recipientName}` : "Good day";
    const from = [signoff.name, signoff.occupation, practice?.practiceName].filter(Boolean).join("\n");
    const ccList = [patientEmail, ...extras]
      .filter(Boolean)
      .filter((a) => norm(a) !== norm(primary))
      .filter((a, i, all) => all.findIndex((b) => norm(b) === norm(a)) === i);
    setEmailPrefill({
      to: primary || "",
      cc: ccList.join("; "),
      subject: `${type.label}: ${who}`,
      message: `${greeting},\n\nPlease find attached the ${type.label.toLowerCase()} for ${who}.\n\nKind regards,\n${from}`,
    });
  };

  // The modal awaits this; a rejection keeps it open with the error shown.
  const doEmail = async (email) => {
    setExporting("email");
    try {
      const res = await exportDocument(patientId, {
        practiceId, type: typeKey, draft: draftForExport(), format: "email", range,
        appointmentId: result?.appointmentId || appointmentId, language, draftId, email,
      });
      const sentTo = res.emailed?.to?.join(", ") || email.to.join(", ");
      // The send and the filing are reported separately: the letter can be
      // out while the copy failed to file, and that must not read as "not
      // sent", or it gets sent twice.
      if (res.filed === false) {
        showToast("error", `Emailed to ${sentTo}. ${res.filingError || "The copy could not be filed in Patient Files. Use Save to Files."}`);
      } else {
        showToast("success", `Emailed to ${sentTo} and filed in Patient Files as ${res.fileName}`);
      }
    } finally {
      setExporting(null);
    }
  };

  // ── Render: set up ────────────────────────────────────────────────────────
  const sourceLine = () => {
    if (customIncomplete) return "Choose both dates.";
    if (sourceLoading || !sourceInfo) return "Counting notes…";
    if (!sourceInfo.count) return "No saved notes in this range.";
    if (sourceInfo.count === 1) return `1 note, ${formatDateGB(sourceInfo.from)}`;
    return `${sourceInfo.count} notes, ${formatDateGB(sourceInfo.from)} to ${formatDateGB(sourceInfo.to)}`;
  };

  const renderSetup = () => (
    <>
      <h5 className="text-primary mb-2">Documents</h5>

      {openDrafts.length > 0 && (
        <div className="posted-invoice-info mb-2">
          <div className="fw-semibold mb-1">
            {openDrafts.length === 1 ? "1 document waiting to be sent" : `${openDrafts.length} documents waiting to be sent`}
          </div>
          {openDrafts.map((d) => (
            <div key={d.draftId} className="d-flex align-items-center gap-2 mb-1">
              <span className="flex-fill">
                {d.title || typeByKey(d.type).label}
                {d.recipientName ? `, ${d.recipientName}` : ""}
                <span className="small text-muted">
                  {" "}
                  {d.automated ? "Prepared from SyncTo" : "Started in the app"}, {formatDateGB((d.updatedAt || d.createdAt || "").slice(0, 10))}
                </span>
              </span>
              <Button
                size="sm"
                variant="outline-primary"
                style={{ width: BUTTON_WIDTH }}
                disabled={loadingDraft}
                onClick={() => setDraftToLoad(d.draftId)}
              >
                <i className="fa-solid fa-pen-to-square me-2" />
                Continue
              </Button>
            </div>
          ))}
        </div>
      )}

      <div className="d-flex flex-wrap gap-2 mb-2">
        {DOCUMENT_TYPES.map((t) => (
          <OverlayTrigger key={t.key} placement="top" overlay={<Tooltip>{t.tip}</Tooltip>}>
            <Button
              size="sm"
              variant={t.key === typeKey ? "primary" : "outline-primary"}
              style={{ width: BUTTON_WIDTH }}
              onClick={() => chooseType(t.key)}
            >
              <i className={`${t.icon} me-2`} />
              {t.button}
            </Button>
          </OverlayTrigger>
        ))}
      </div>
      <p className="small text-muted mb-3">{type.help}</p>

      <Row className="gy-3">
        <Col xs={12} xl={5}>
          <h6 className="mb-2 text-primary fw-bold">What to include</h6>

          <Form.Group className="mb-2">
            <Form.Label>Language</Form.Label>
            <div className="d-flex flex-wrap gap-2">
              {LANGUAGE_OPTIONS.map((l) => (
                <Button
                  key={l.key}
                  size="sm"
                  variant={language === l.key ? "secondary" : "outline-secondary"}
                  style={{ width: BUTTON_WIDTH }}
                  aria-pressed={language === l.key}
                  onClick={() => setLanguage(l.key)}
                >
                  {l.label}
                </Button>
              ))}
            </div>
          </Form.Group>

          <Form.Group className="mb-2">
            <Form.Label>History</Form.Label>
            <div className="d-flex flex-wrap gap-2">
              {RANGE_OPTIONS.map((r) => (
                <Button
                  key={r.key}
                  size="sm"
                  variant={rangeMode === r.key ? "secondary" : "outline-secondary"}
                  style={{ width: BUTTON_WIDTH }}
                  aria-pressed={rangeMode === r.key}
                  onClick={() => setRangeMode(r.key)}
                >
                  {r.label}
                </Button>
              ))}
            </div>
            {rangeMode === "custom" && (
              <div className="d-flex gap-2 mt-2">
                <Form.Group className="flex-fill">
                  <Form.Label>From</Form.Label>
                  <Form.Control type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} />
                </Form.Group>
                <Form.Group className="flex-fill">
                  <Form.Label>To</Form.Label>
                  <Form.Control type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} />
                </Form.Group>
              </div>
            )}
          </Form.Group>

          <div className="posted-invoice-info mb-2">
            <div className="d-flex align-items-center gap-2">
              <i className="fa-solid fa-circle-info text-primary" />
              <span>{sourceLine()}</span>
              {sourceLoading && <Spinner size="sm" animation="border" />}
            </div>
            {/* Last note is one note: say which. The other ranges are exactly
                the window chosen, so nothing is forced in. */}
            {sourceInfo?.anchor && rangeMode === "this_note" && (
              <div className="small mt-1">
                {appointmentId && !sourceInfo.anchorMissing
                  ? `Uses the note from this appointment (${formatDateGB(sourceInfo.anchor.date)}).`
                  : `Uses the latest note (${formatDateGB(sourceInfo.anchor.date)}).`}
              </div>
            )}
            {sourceInfo?.anchorMissing && rangeMode === "this_note" && (
              <div className="small mt-1">
                No note has been saved for this appointment yet, so the most recent note is used instead.
              </div>
            )}
          </div>
        </Col>

        <Col xs={12} xl={7}>
          <h6 className="mb-2 text-primary fw-bold">{type.label}</h6>

          {type.hasRecipient && (
            <Row className="gy-2 mb-2">
              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.Label>To</Form.Label>
                  <RecipientSearch
                    value={recipientName}
                    onChange={(v) => { setRecipientName(v); setRecipientPersonId(null); }}
                    onPick={pickRecipient}
                    people={people}
                    placeholder="Type a name to search People, or enter one"
                  />
                </Form.Group>
              </Col>
              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.Label>Practice or discipline</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Orthopaedic Surgeon, Vincent"
                    value={recipientDetail}
                    onChange={(e) => setRecipientDetail(e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
          )}

          {typeKey === "referral" && (
            <>
              <Form.Group className="mb-2">
                <Form.Label>Reason for referral and what you are asking for</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Persistent left knee pain despite six weeks of treatment. Please assess for a meniscal tear."
                  value={ask}
                  onChange={(e) => setAsk(e.target.value)}
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Urgency</Form.Label>
                <div className="d-flex gap-2">
                  {[["routine", "Routine"], ["urgent", "Urgent"]].map(([k, l]) => (
                    <Button
                      key={k}
                      size="sm"
                      variant={urgency === k ? "secondary" : "outline-secondary"}
                      style={{ width: BUTTON_WIDTH }}
                      aria-pressed={urgency === k}
                      onClick={() => setUrgency(k)}
                    >
                      {l}
                    </Button>
                  ))}
                </div>
              </Form.Group>
            </>
          )}

          {typeKey === "progress_report" && (
            <>
              {templates.length > 0 && (
                <Form.Group className="mb-2">
                  <Form.Label>Report template</Form.Label>
                  <Form.Select value={templateId} onChange={(e) => applyTemplate(e.target.value)}>
                    <option value="">Default headings</option>
                    {templates.map((t) => (
                      <option key={t._id} value={t._id}>{t.templateTitle}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              )}
              <Form.Group className="mb-2">
                <Form.Label>Sections</Form.Label>
                <div className="d-flex flex-wrap gap-2 mb-2">
                  {headings.map((h, i) => (
                    <Badge key={`${h}-${i}`} bg="light" text="dark" className="border d-inline-flex align-items-center gap-2 fw-normal">
                      {h}
                      <OverlayTrigger placement="top" overlay={<Tooltip>Remove section</Tooltip>}>
                        <Button
                          variant="link"
                          className="text-primary text-decoration-none p-0"
                          style={{ width: 24, height: 24, lineHeight: 1 }}
                          aria-label={`Remove ${h}`}
                          onClick={() => setHeadings((prev) => prev.filter((_x, j) => j !== i))}
                        >
                          <i className="fa-solid fa-xmark" />
                        </Button>
                      </OverlayTrigger>
                    </Badge>
                  ))}
                </div>
                <div className="d-flex flex-wrap gap-2">
                  <Form.Control
                    type="text"
                    placeholder="Add a section"
                    style={{ width: BUTTON_WIDTH }}
                    value={newHeading}
                    onChange={(e) => setNewHeading(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && newHeading.trim()) {
                        e.preventDefault();
                        setHeadings((prev) => [...prev, newHeading.trim()]);
                        setNewHeading("");
                      }
                    }}
                  />
                  <Button
                    variant="outline-secondary"
                    style={{ width: ADD_BUTTON_WIDTH }}
                    disabled={!newHeading.trim()}
                    onClick={() => { setHeadings((prev) => [...prev, newHeading.trim()]); setNewHeading(""); }}
                  >
                    <i className="fa-solid fa-plus me-2" />
                    Add
                  </Button>
                </div>
              </Form.Group>
            </>
          )}

          {typeKey === "sick_note" && (
            <>
              <Row className="gy-2 mb-2">
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label>Examined on</Form.Label>
                    <Form.Control type="date" value={examinedOn} onChange={(e) => setExaminedOn(e.target.value)} />
                  </Form.Group>
                </Col>
                <Col xs={6} md={4}>
                  <Form.Group>
                    <Form.Label>Unfit from</Form.Label>
                    <Form.Control type="date" value={unfitFrom} onChange={(e) => setUnfitFrom(e.target.value)} />
                  </Form.Group>
                </Col>
                <Col xs={6} md={4}>
                  <Form.Group>
                    <Form.Label>Unfit to</Form.Label>
                    <Form.Control
                      type="date"
                      value={unfitTo}
                      min={unfitFrom || undefined}
                      isInvalid={!!unfitTo && !!unfitFrom && unfitTo < unfitFrom}
                      onChange={(e) => setUnfitTo(e.target.value)}
                    />
                    <Form.Control.Feedback type="invalid">The last day is before the first.</Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
              <Form.Group className="mb-2">
                <Form.Label>Fitness</Form.Label>
                <div className="d-flex flex-wrap gap-2">
                  {FITNESS_OPTIONS.map((f) => (
                    <Button
                      key={f.key}
                      size="sm"
                      variant={fitness === f.key ? "secondary" : "outline-secondary"}
                      style={{ width: BUTTON_WIDTH }}
                      aria-pressed={fitness === f.key}
                      onClick={() => setFitness(f.key)}
                    >
                      {f.label}
                    </Button>
                  ))}
                </div>
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Check
                  type="switch"
                  id="include-condition"
                  label="Include the nature of the condition"
                  checked={includeCondition}
                  onChange={(e) => setIncludeCondition(e.target.checked)}
                />
                <div className="small text-muted">
                  Not required by the BCEA. Include it only with the patient's consent.
                </div>
              </Form.Group>
              {includeCondition && (
                <Form.Group className="mb-2">
                  <Form.Label>Condition, in plain terms</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Leave blank to draft one sentence from the note"
                    value={conditionText}
                    onChange={(e) => setConditionText(e.target.value)}
                  />
                </Form.Group>
              )}
            </>
          )}

          {typeKey === "return_to_work" && (
            <>
              <Row className="gy-2 mb-2">
                <Col xs={12} md={6}>
                  <Form.Group>
                    <Form.Label>Examined on</Form.Label>
                    <Form.Control type="date" value={examinedOn} onChange={(e) => setExaminedOn(e.target.value)} />
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group>
                    <Form.Label>Fit to return from</Form.Label>
                    <Form.Control type="date" value={fitFrom} onChange={(e) => setFitFrom(e.target.value)} />
                  </Form.Group>
                </Col>
              </Row>
              <Form.Group className="mb-2">
                <Form.Label>Restrictions</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  placeholder="No lifting over 10 kg for two weeks. Leave blank if none."
                  value={restrictions}
                  onChange={(e) => setRestrictions(e.target.value)}
                />
              </Form.Group>
            </>
          )}

          {typeKey === "motivation" && (
            <>
              <div className="posted-invoice-info mb-2">
                <i className="fa-solid fa-circle-info text-primary me-2" />
                {patient?.patientMedicalAid
                  ? `Addressed to ${patient.patientMedicalAid}${patient.patientMedicalAidNumber ? `, member no ${patient.patientMedicalAidNumber}` : ""}. ICD-10 codes come from Diagnoses.`
                  : "No medical aid on the patient record. Add it under Edit Patient so the motivation is addressed correctly."}
              </div>
              <Form.Group className="mb-2">
                <Form.Label>What are you motivating for?</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="8 additional physiotherapy sessions, code 72501"
                  value={requestFor}
                  onChange={(e) => setRequestFor(e.target.value)}
                />
              </Form.Group>
              <Row className="gy-2 mb-2">
                <Col xs={12} md={5}>
                  <Form.Group>
                    <Form.Label>Scheme reference or authorisation number</Form.Label>
                    <Form.Control type="text" value={referenceNumber} onChange={(e) => setReferenceNumber(e.target.value)} />
                  </Form.Group>
                </Col>
                <Col xs={12} md={7}>
                  <Form.Group>
                    <Form.Label>Your justification</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      placeholder="Points the motivation must make. Optional."
                      value={justification}
                      onChange={(e) => setJustification(e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </>
          )}

          {typeKey === "imaging_request" && (
            <>
              <Row className="gy-2 mb-2">
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label>Modality</Form.Label>
                    <Form.Select value={modality} onChange={(e) => setModality(e.target.value)}>
                      {MODALITY_OPTIONS.map((m) => <option key={m} value={m}>{m}</option>)}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col xs={12} md={8}>
                  <Form.Group>
                    <Form.Label>Region</Form.Label>
                    <Form.Control type="text" placeholder="Left knee" value={region} onChange={(e) => setRegion(e.target.value)} />
                  </Form.Group>
                </Col>
              </Row>
              <Form.Group className="mb-2">
                <Form.Label>Clinical question</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  placeholder="Meniscal tear? Six weeks of mechanical symptoms after a twisting injury."
                  value={clinicalQuestion}
                  onChange={(e) => setClinicalQuestion(e.target.value)}
                />
              </Form.Group>
            </>
          )}

          {errorAlert}

          <div className="d-flex align-items-center gap-2 mt-3">
            <Button variant="primary" onClick={generate} disabled={!canGenerate} style={{ width: BUTTON_WIDTH }}>
              {drafting ? <Spinner size="sm" animation="border" /> : (
                <>
                  <i className="fa-solid fa-wand-magic-sparkles me-2" />
                  Generate
                </>
              )}
            </Button>
            {drafting && (
              <span className="small text-muted">
                {type.form ? "Preparing the certificate…" : "Reading the notes and writing the draft…"}
              </span>
            )}
            {!drafting && !sourceLoading && !type.form && sourceInfo && !sourceInfo.count && (
              <span className="small text-muted">
                Save a note first, or widen the history: this document is written from the notes.
              </span>
            )}
          </div>
        </Col>
      </Row>
    </>
  );

  // ── Render: review ────────────────────────────────────────────────────────
  const renderReview = () => {
    const s = result?.sources || {};
    // Certificates come from the typed dates; a note only contributes the
    // optional condition line, so the banner never credits the notes.
    const sourcesText = type.form || !s.count
      ? "the details you entered"
      : s.count === 1
        ? `1 note, ${formatDateGB(s.from)}`
        : `${s.count} notes, ${formatDateGB(s.from)} to ${formatDateGB(s.to)}`;
    const signoff = result?.signoff || {};
    const busy = !!exporting;
    const showRecipient = type.hasRecipient || type.editableRecipient;

    return (
      <>
        <h5 className="text-primary mb-2">{type.label}: {patientName}</h5>

        <div className="posted-invoice-info mb-3">
          <i className="fa-solid fa-circle-info text-primary me-2" />
          {result?.automated
            ? `Prepared automatically from ${sourcesText} because the recording mentioned it. Check names, dates and the request before you send it. Downloading it marks the task done.`
            : `${type.form ? "Built from" : "Drafted from"} ${sourcesText}. Check names, dates and the request before you send it.`}
        </div>

        {errorAlert}

        {showRecipient && (
          <Row className="gy-2 mb-2">
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label>To</Form.Label>
                <RecipientSearch
                  value={edited.recipientName}
                  onChange={(v) => setEdited({ ...edited, recipientName: v, recipientPersonId: null })}
                  onPick={(p) => setEdited({ ...edited, recipientName: personDisplayName(p), recipientDetail: personDetail(p), recipientPersonId: personIdOf(p) })}
                  people={people}
                  placeholder="Type a name to search People, or enter one"
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label>Practice or discipline</Form.Label>
                <Form.Control type="text" value={edited.recipientDetail} onChange={(e) => setEdited({ ...edited, recipientDetail: e.target.value })} />
              </Form.Group>
            </Col>
          </Row>
        )}

        <Row className="gy-2 mb-2">
          <Col xs={12} md={6}>
            <Form.Group>
              <Form.Label>Title</Form.Label>
              <Form.Control type="text" value={edited.title} onChange={(e) => setEdited({ ...edited, title: e.target.value })} />
            </Form.Group>
          </Col>
          {showRecipient && (
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label>Salutation</Form.Label>
                <Form.Control type="text" value={edited.salutation} onChange={(e) => setEdited({ ...edited, salutation: e.target.value })} />
              </Form.Group>
            </Col>
          )}
        </Row>

        {/* One memo for the whole body. Bold lines are the section headings
            the PDF and Word file print in bold; everything beneath a heading
            belongs to it. */}
        <Form.Group className="mb-3">
          <Form.Label>Content</Form.Label>
          <div className="acc-doc-editor bg-white rounded">
            <ReactQuill
              theme="snow"
              value={edited.body || ""}
              onChange={(html) => setEdited((prev) => ({ ...prev, body: html }))}
              modules={BODY_TOOLBAR}
              formats={BODY_FORMATS}
              placeholder="Write the document here. A bold line starts a new section."
            />
          </div>
          <div className="small text-muted mt-1">A line in bold becomes a section heading in the document.</div>
        </Form.Group>

        {showRecipient && (
          <Form.Group className="mb-3" style={{ maxWidth: 320 }}>
            <Form.Label>Closing</Form.Label>
            <Form.Control type="text" value={edited.closing} onChange={(e) => setEdited({ ...edited, closing: e.target.value })} />
          </Form.Group>
        )}

        <div className="mb-3">
          <span className="fw-semibold">{signoff.name}</span>
          <div className="small text-muted">
            {[signoff.occupation, signoff.registration, signoff.practiceNumber].filter(Boolean).join(" · ")}
          </div>
          <div className="small text-muted">Signed with your profile details. The date is added when you download.</div>
        </div>

        {/* Back, then the ways out of the review in the order they are used:
            Email is the one dominant action, the downloads and Save are the
            alternatives, Regenerate starts over. */}
        <div className="d-flex flex-wrap gap-2 position-sticky bottom-0 bg-white py-2 border-top">
          <Button variant="outline-secondary" onClick={() => setStep("setup")} disabled={busy} style={{ width: BUTTON_WIDTH }}>
            <i className="fa-solid fa-angle-left me-2" />
            Back
          </Button>
          <Button variant="primary" onClick={openEmail} disabled={busy} style={{ width: BUTTON_WIDTH }}>
            {exporting === "email" ? <Spinner size="sm" animation="border" /> : (<><i className="fa-solid fa-envelope me-2" />Email</>)}
          </Button>
          <Button variant="outline-primary" onClick={() => doExport("pdf")} disabled={busy} style={{ width: BUTTON_WIDTH }}>
            {exporting === "pdf" ? <Spinner size="sm" animation="border" /> : (<><i className="fa-solid fa-download me-2" />Download PDF</>)}
          </Button>
          <Button variant="outline-primary" onClick={() => doExport("docx")} disabled={busy} style={{ width: BUTTON_WIDTH }}>
            {exporting === "docx" ? <Spinner size="sm" animation="border" /> : (<><i className="fa-solid fa-file-word me-2" />Download Word</>)}
          </Button>
          <Button variant="outline-primary" onClick={() => doExport("save")} disabled={busy} style={{ width: BUTTON_WIDTH }}>
            {exporting === "save" ? <Spinner size="sm" animation="border" /> : (<><i className="fa-solid fa-cabinet-filing me-2" />Save to Files</>)}
          </Button>
          <Button variant="outline-secondary" onClick={regenerate} disabled={busy || drafting} style={{ width: BUTTON_WIDTH }}>
            {drafting ? <Spinner size="sm" animation="border" /> : (<><i className="fa-solid fa-rotate-right me-2" />Regenerate</>)}
          </Button>
        </div>
        <div className="small text-muted mt-1">Email and Save to Files put a PDF copy in Patient Files. Downloads do not.</div>

        <SendDocumentModal
          show={!!emailPrefill}
          onHide={() => setEmailPrefill(null)}
          title={`Email ${type.label}: ${patientName}`}
          people={people}
          initialTo={emailPrefill?.to}
          initialCc={emailPrefill?.cc}
          initialSubject={emailPrefill?.subject}
          initialMessage={emailPrefill?.message}
          onSend={doEmail}
        />
      </>
    );
  };

  return (
    <Container fluid className="pt-2 acc-list">
      {loadingDraft ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Loading document…</p>
        </div>
      ) : step === "review" && edited ? renderReview() : renderSetup()}
      <AppToast show={toast.show} onClose={hideToast} variant={toast.variant} message={toast.message} />
    </Container>
  );
}
